import merge from 'lodash/merge'

import { initialState } from '..'
import {
  HIDE_DIALOG,
  HIDE_FLASH_MESSAGE,
  SET_JOB_ERROR,
  SET_CREATION_STATUS_OF_DRAFT,
  SET_SUBMISSION_STATUS_OF_DRAFT,
  SET_LOGO_UPLOADING_STATUS,
  SET_JOB_SUSPENSION_OR_RESUMPTION_STATUS,
  SET_USER_INFO_SUBMISSION_STATUS,
  SET_USER_SETTINGS_SUBMISSION_STATUS,
  SET_JOB_EDITING_STATUS,
  SHOW_DIALOG,
  SET_DIALOG_PENDING_STATUS,
  SET_CURRENT_FILTER,
  SET_CURRENT_PAGE,
  SET_JOBS_FETCHING_ALL_STATUS,
  SHOW_UPGRADES_FOR_JOB,
  HIDE_UPGRADES_FOR_JOB,
  SET_DEFAULT_LOGO_UPLOADING_STATUS,
  SET_SEARCH_QUERY,
  REMOVE_FILTER,
  ADD_FILTER,
  START_WAITING,
  STOP_WAITING,
  DISPLAY_PRODUCT_MODAL,
  HIDE_PRODUCT_MODAL,
  ADD_TOASTER,
  REMOVE_TOASTER,
  SET_SUGGESTED_SALARY_MINMAX
} from '../../actions/ui/ui'
import { REQUEST_UPGRADE, UPGRADE_FAILED, SET as SET_JOBS } from '../../actions/jobs/jobs'

import {
  Modal,
  SetCreationStatusOfDraftAction,
  SetSubmissionStatusOfDraftAction,
  SetLogoUploadingStatusAction,
  SetJobEditingStatusAction,
  SetCurrentFilterAction,
  SetCurrentPageAction,
  SetTotalPageCountAction,
  SetJobsFetchingAllStatusAction,
  SetSearchQueryAction,
  AddFilterAction,
  UiActions,
} from '../../actions/ui/ui'
import { ProductUpgrade } from '../entitlement/entitlement'
import { JobsActions } from '../../actions/jobs/jobs'
import { WebshopActions } from '../../actions/webshop/webshop'
import { SiteAlias } from '../../../schema/webshop-package'
import { ToasterVariants } from '../../actions/ui/ui'

type JobStatus = {
  isLoading?: boolean,
  hasFailed?: boolean,
}

export type JobStatuses = {
  status: {
    [key: string]: {
      suspensionOrResumption?: JobStatus,
    },
  },
  currentPage: SetCurrentPageAction['payload'],
  currentFilter: SetCurrentFilterAction['payload'],
  currentFilters: AddFilterAction['payload'][],
  currentSearchQuery: SetSearchQueryAction['payload'],
  isFetchingAll: SetJobsFetchingAllStatusAction['payload'],
  isCreating: SetCreationStatusOfDraftAction['payload'],
  isSubmitting: SetSubmissionStatusOfDraftAction['payload'],
  isLogoUploading: SetLogoUploadingStatusAction['payload'],
  isAboutToBeEdited: SetJobEditingStatusAction['payload'],
  totalPageCount: SetTotalPageCountAction['payload'],
  jobIdWithShownUpgrades: string | undefined,
  requestedJobUpgrades: {
    [key: string]: {
      status: string,
      upgrade: ProductUpgrade,
      playhead: number,
    },
  },
}

export type Toaster = {
  id: string,
  variant: ToasterVariants,
  title: string,
  description: string
}

export type UiStoreType = {
  flashMessage: {
    type: string,
  },
  modal: Modal & {
    showDialog: boolean,
    isPending: boolean,
  },
  job: JobStatuses,
  user: {
    isSubmittingInfo: boolean,
    isSubmittingSettings: boolean,
  },
  account: {
    isDefaultLogoUploading: boolean,
  },
  suggestions: {
    selectedSites: Array<SiteAlias>,
  },
  inProgressElementId: string,
  displayProductModal: boolean,
  toasters: Toaster[],
  suggestedSalaryMin: number | undefined,
  suggestedSalaryMax: number | undefined,
}

const reducer = (
  state: UiStoreType | undefined,
  action: UiActions | JobsActions | WebshopActions,
): UiStoreType => {
  if (!state) return initialState.ui

  if (action.type === SET_JOB_SUSPENSION_OR_RESUMPTION_STATUS) {
    const { id, isLoading } = action.payload
    return merge({}, state, {
      job: { status: { [id]: { suspensionOrResumption: { isLoading } } } },
    })
  }

  if (action.type === SET_JOB_ERROR) {
    const { id, type: errorType, value } = action.payload
    return merge({}, state, {
      job: { status: { [id]: { [errorType]: { hasFailed: value } } } },
    })
  }

  if (action.type === SHOW_DIALOG) {
    return {
      ...state,
      modal: {
        ...state.modal,
        ...action.payload,
        showDialog: true,
      },
    }
  }

  if (action.type === HIDE_DIALOG) {
    return {
      ...state,
      modal: {
        ...state.modal,
        showDialog: false,
      },
    }
  }

  if (action.type === HIDE_FLASH_MESSAGE) {
    return {
      ...state,
      flashMessage: {
        type: '',
      },
    }
  }

  if (action.type === SET_CREATION_STATUS_OF_DRAFT) {
    return {
      ...state,
      job: {
        ...state.job,
        isCreating: action.payload,
      },
    }
  }

  if (action.type === SET_SUBMISSION_STATUS_OF_DRAFT) {
    return {
      ...state,
      job: {
        ...state.job,
        isSubmitting: action.payload,
      },
    }
  }

  if (action.type === SET_LOGO_UPLOADING_STATUS) {
    return {
      ...state,
      job: {
        ...state.job,
        isLogoUploading: action.payload,
      },
    }
  }

  if (action.type === SET_USER_INFO_SUBMISSION_STATUS) {
    return {
      ...state,
      user: {
        ...state.user,
        isSubmittingInfo: action.payload,
      },
    }
  }

  if (action.type === SET_USER_SETTINGS_SUBMISSION_STATUS) {
    return {
      ...state,
      user: {
        ...state.user,
        isSubmittingSettings: action.payload,
      },
    }
  }

  if (action.type === SET_JOB_EDITING_STATUS) {
    return {
      ...state,
      job: {
        ...state.job,
        isAboutToBeEdited: action.payload,
      },
    }
  }

  if (action.type === SET_DIALOG_PENDING_STATUS) {
    return {
      ...state,
      modal: {
        ...state.modal,
        isPending: action.payload,
      },
    }
  }

  if (action.type === SET_CURRENT_FILTER) {
    return {
      ...state,
      job: {
        ...state.job,
        currentFilter: action.payload,
        currentPage: 1,
      },
    }
  }

  if (action.type === ADD_FILTER) {
    const tmp = (state.job.currentFilters).slice()
    const index = tmp.indexOf(action.payload)
    if (index === -1 && action.payload) {
      tmp.push(action.payload)
    }
    return {
      ...state,
      job: {
        ...state.job,
        currentFilters: tmp,
        currentPage: 1,
      },
    }
  }

  if (action.type === REMOVE_FILTER) {
    const tmp = (state.job.currentFilters).slice()
    const index = tmp.indexOf(action.payload)
    if (index > -1) {
      tmp.splice(index, 1)
    }
    return {
      ...state,
      job: {
        ...state.job,
        currentFilters: tmp,
        currentPage: 1,
      },
    }
  }

  if (action.type === SET_SEARCH_QUERY) {
    return {
      ...state,
      job: {
        ...state.job,
        currentSearchQuery: action.payload,
        currentPage: 1,
      },
    }
  }

  if (action.type === SET_CURRENT_PAGE) {
    return {
      ...state,
      job: {
        ...state.job,
        currentPage: action.payload,
      },
    }
  }

  if (action.type === SET_JOBS) {
    return {
      ...state,
      job: {
        ...state.job,
        totalPageCount: action.payload.totalPageCount,
      },
    }
  }

  if (action.type === SET_JOBS_FETCHING_ALL_STATUS) {
    return {
      ...state,
      job: {
        ...state.job,
        isFetchingAll: action.payload,
      },
    }
  }

  if (action.type === SHOW_UPGRADES_FOR_JOB) {
    return {
      ...state,
      job: {
        ...state.job,
        jobIdWithShownUpgrades: action.payload,
      },
    }
  }

  if (action.type === HIDE_UPGRADES_FOR_JOB) {
    return {
      ...state,
      job: {
        ...state.job,
        jobIdWithShownUpgrades: null,
      },
    }
  }

  if (action.type === REQUEST_UPGRADE) {
    return {
      ...state,
      job: {
        ...state.job,
        requestedJobUpgrades: {
          ...state.job.requestedJobUpgrades,
          [action.payload.jobId]: {
            status: 'pending',
            upgrade: action.payload.upgrade,
            playhead: action.payload.playhead,
          },
        },
      },
    }
  }

  if (action.type === UPGRADE_FAILED) {
    return {
      ...state,
      job: {
        ...state.job,
        requestedJobUpgrades: {
          ...state.job.requestedJobUpgrades,
          [action.payload.jobId]: {
            status: 'rejected',
            upgrade: action.payload.upgrade,
            playhead: action.payload.playhead,
          },
        },
      },
    }
  }

  if (action.type === SET_DEFAULT_LOGO_UPLOADING_STATUS) {
    return {
      ...state,
      account: {
        isDefaultLogoUploading: action.payload,
      },
    }
  }

  if (action.type === START_WAITING) {
    return {
      ...state,
      inProgressElementId: action.payload
    }
  }

  if (action.type === STOP_WAITING) {
    return {
      ...state,
      inProgressElementId: ''
    }
  }

  if (action.type === DISPLAY_PRODUCT_MODAL) {
    return {
      ...state,
      displayProductModal: true
    }
  }

  if (action.type === HIDE_PRODUCT_MODAL) {
    return {
      ...state,
      displayProductModal: false
    }
  }

  if (action.type === ADD_TOASTER) {
    return {
      ...state,
      toasters: [...state.toasters, action.payload]
    }
  }

  if (action.type === SET_SUGGESTED_SALARY_MINMAX) {
    return {
      ...state,
      suggestedSalaryMin: action.payload.min,
      suggestedSalaryMax: action.payload.max,
    }
  }

  // istanbul ignore else
  if (action.type === REMOVE_TOASTER) {
    return {
      ...state,
      toasters: state.toasters.filter(toaster => toaster.id !== action.payload)
    }
  }

  return state
}

export default reducer
